import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { capitalize } from "lodash";

import AppFullscreenModal from "../AppFullscreenModal";
import AuthField from "../Auth/AuthField";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import { TemplatesTable, listTemplateParamSlots } from "./TemplatesTable";
import TemplateConversationPreview from "../TemplateConversationPreview";
import ModalImageChatbot from "../ModalImageChatbot";
import { VariablesModal } from "./VariablesModal";
import { resolveUploadedFileUrl } from "../../helpers/resolveMediaUrl";

const defaultVariables = ["contactName"];

const createInitialState = () => ({
	body: "",
	contactId: "",
	contact: null,
	sendAt: moment().add(1, "hour").format("YYYY-MM-DDTHH:mm"),
	sentAt: "",
	connectionId: "",
	connection: null,
	templateId: null,
	template: null,
	templateParams: [],
	mediaPath: null,
	ticketId: null,
	status: "",
});

function IconAttach() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
		</svg>
	);
}

function isWabaScheduleChannel(channel) {
	const c = String(channel || "").toLowerCase();
	return (
		c === "whatsappcloud" ||
		c === "whatsappbusiness" ||
		c === "whatsapp_business_account"
	);
}

function isWhatsAppScheduleChannel(channel) {
	const c = String(channel || "").toLowerCase();
	return c === "whatsapp" || isWabaScheduleChannel(c);
}

function getTemplateFormat(template) {
	const header = template?.components?.find(
		(c) => String(c?.type || "").toUpperCase() === "HEADER"
	);
	return String(header?.format || "").toUpperCase();
}

function templateNeedsMedia(format) {
	return ["VIDEO", "DOCUMENT", "IMAGE"].includes(format);
}

function mediaAccept(format) {
	if (format === "IMAGE") return "image/png,image/jpeg,image/jpg";
	if (format === "VIDEO") return "video/mp4";
	if (format === "DOCUMENT") return "application/pdf";
	return "image/png,image/jpeg,image/jpg,video/mp4,application/pdf";
}

function isImageUrl(url) {
	return /\.(png|jpe?g|gif|webp)(\?.*)?$/i.test(url || "");
}

function renderContactLabel(contact) {
	if (!contact) return "";
	return [contact.name, contact.number, contact.channel].filter(Boolean).join(" - ");
}

export function buildScheduleTemplatePreview(template, params = []) {
	const name = String(template?.name || "").trim();
	const bodyText = String(
		template?.components?.find(
			(component) => String(component?.type || "").toUpperCase() === "BODY"
		)?.text || ""
	).trim();
	let text = bodyText || name;
	(Array.isArray(params) ? params : []).forEach((value, index) => {
		text = text.replace(
			new RegExp(`\\{\\{\\s*${index + 1}\\s*\\}\\}`, "g"),
			String(value ?? "")
		);
	});
	text = String(text || "").trim();
	if (name && bodyText) return `[${name}] ${text}`;
	return text || name || "Template";
}

function ScheduleModal({ open, onClose, scheduleId, reload, contact, contactId }) {
	const { user } = useContext(AuthContext);
	const { darkMode } = useContext(ThemeContext);
	const [scheduleData, setScheduleData] = useState(createInitialState);
	const [contacts, setContacts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingContacts, setLoadingContacts] = useState(false);
	const [loadingConnections, setLoadingConnections] = useState(false);
	const [contactQuery, setContactQuery] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [variablesModalOpen, setVariablesModalOpen] = useState(false);
	const [connectionOptions, setConnectionOptions] = useState([]);
	const [templateParamsLength, setTemplateParamsLength] = useState(0);
	const [templateParams, setTemplateParams] = useState([]);
	const lastTemplateIdRef = useRef(null);

	const whatsappConnections = useMemo(
		() =>
			connectionOptions.filter((item) =>
				isWhatsAppScheduleChannel(item?.channel)
			),
		[connectionOptions]
	);

	const isTemplateMode = isWabaScheduleChannel(scheduleData?.connection?.channel);
	const currentTemplateFormat = getTemplateFormat(scheduleData?.template);
	const requiresTemplateMedia = templateNeedsMedia(currentTemplateFormat);
	const templateParamSlots = useMemo(
		() => listTemplateParamSlots(scheduleData?.template?.components || []),
		[scheduleData?.template]
	);
	const isLockedContact = Boolean(scheduleId || contact?.id || contactId);

	const handleTemplateResolved = useCallback((template) => {
		if (!template) return;
		setScheduleData((prevState) => {
			if (
				prevState.template &&
				String(prevState.template.id) === String(template.id)
			) {
				return prevState;
			}
			return { ...prevState, template };
		});
	}, []);

	useEffect(() => {
		if (!open) return undefined;
		if (user?.companyId == null) {
			setConnectionOptions([]);
			return undefined;
		}

		let cancelled = false;
		setLoadingConnections(true);

		(async () => {
			try {
				const { data } = await apiV2.get("/connections", {
					params: { companyId: user.companyId },
				});
				if (!cancelled) {
					setConnectionOptions(Array.isArray(data) ? data : []);
				}
			} catch (err) {
				toastError(err);
				if (!cancelled) setConnectionOptions([]);
			} finally {
				if (!cancelled) setLoadingConnections(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open, user?.companyId]);

	useEffect(() => {
		if (!open || !contact?.id) return;

		setScheduleData((prevState) => ({
			...prevState,
			contactId: contact.id,
			contact,
		}));
		setContactQuery(renderContactLabel(contact));
	}, [open, contact]);

	useEffect(() => {
		if (!open || !contactId || contact?.id || scheduleId) return undefined;

		let cancelled = false;
		setLoading(true);

		(async () => {
			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (cancelled) return;
				setScheduleData((prevState) => ({
					...prevState,
					contactId: data.id,
					contact: data,
				}));
				setContactQuery(renderContactLabel(data));
			} catch (err) {
				if (!cancelled) toastError(err);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open, contactId, contact?.id, scheduleId]);

	useEffect(() => {
		if (!open || !scheduleId || user?.companyId == null) return undefined;

		let cancelled = false;
		setLoading(true);

		(async () => {
			try {
				const { data } = await apiV2.get(`/schedules/${scheduleId}`, {
					params: { companyId: user.companyId },
				});

				if (cancelled) return;

				const nextState = {
					...createInitialState(),
					...data,
					sendAt: data.sendAt ? moment(data.sendAt).format("YYYY-MM-DDTHH:mm") : createInitialState().sendAt,
					connectionId: data.connectionId || data.connection?.id || "",
					connection: data.connection || null,
					contactId: data.contactId || data.contact?.id || "",
					contact: data.contact || null,
				};

				setScheduleData(nextState);
				setContactQuery(renderContactLabel(data.contact));

				let parsedTemplateParams = [];
				if (data?.templateParams) {
					try {
						parsedTemplateParams = JSON.parse(data.templateParams) || [];
					} catch {
						parsedTemplateParams = [];
					}
				}

				setTemplateParams(parsedTemplateParams);
				setTemplateParamsLength(parsedTemplateParams.length || 0);
			} catch (err) {
				if (!cancelled) toastError(err);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open, scheduleId, user?.companyId]);

	useEffect(() => {
		if (
			!open ||
			isLockedContact ||
			contactQuery.trim().length < 3 ||
			!scheduleData?.connection?.channel
		) {
			setLoadingContacts(false);
			setContacts([]);
			return undefined;
		}

		if (
			scheduleData.contact &&
			renderContactLabel(scheduleData.contact).toLowerCase() ===
				contactQuery.toLowerCase()
		) {
			setContacts([]);
			return undefined;
		}

		let cancelled = false;
		setLoadingContacts(true);

		const delayDebounceFn = setTimeout(async () => {
			try {
				const {
					data: { contacts: contactList },
				} = await api.get("contacts", {
					params: {
						searchParam: contactQuery,
						channel: scheduleData?.connection?.channel,
					},
				});

				if (cancelled) return;
				setContacts(
					(Array.isArray(contactList) ? contactList : []).map((item) => ({
						id: item.id,
						name: item.name,
						number: item.number,
						channel: item.channel,
					}))
				);
			} catch (err) {
				if (!cancelled) toastError(err);
			} finally {
				if (!cancelled) setLoadingContacts(false);
			}
		}, 400);

		return () => {
			cancelled = true;
			clearTimeout(delayDebounceFn);
		};
	}, [open, contactQuery, scheduleData?.connection?.channel, scheduleData.contact, isLockedContact]);

	useEffect(() => {
		if (!scheduleData.templateId) {
			lastTemplateIdRef.current = null;
			setTemplateParamsLength(0);
			setTemplateParams([]);
			return;
		}

		if (!scheduleData.template) {
			return;
		}

		const nextLength = listTemplateParamSlots(
			scheduleData.template.components || []
		).length;
		const previousId = lastTemplateIdRef.current;
		lastTemplateIdRef.current = scheduleData.templateId;
		setTemplateParamsLength(nextLength);

		if (!previousId) {
			setTemplateParams((prev) => {
				if (prev.length === nextLength) return prev;
				return Array.from({ length: nextLength }, (_, index) => prev[index] || "");
			});
			return;
		}

		if (String(previousId) !== String(scheduleData.templateId)) {
			const nextParams = Array.from({ length: nextLength }, () => "");
			setTemplateParams(nextParams);
			setScheduleData((prevState) => ({
				...prevState,
				templateParams: nextParams,
				mediaPath: null,
			}));
		}
	}, [scheduleData.templateId, scheduleData.template]);

	const handleClose = () => {
		onClose();
		setScheduleData(createInitialState());
		setContacts([]);
		setContactQuery("");
		setLoading(false);
		setLoadingContacts(false);
		setLoadingConnections(false);
		setIsSubmitting(false);
		setTemplateParams([]);
		setTemplateParamsLength(0);
		lastTemplateIdRef.current = null;
	};

	const handleMessage = (event) => {
		const { value } = event.target;
		if (value[value.length - 1] === "$") {
			setVariablesModalOpen(true);
			return;
		}

		setScheduleData((prevState) => ({
			...prevState,
			body: value,
		}));
	};

	const handleUpdateMedia = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		const formData = new FormData();
		formData.append("file", file);

		try {
			const response = await api.post("/bulk-messaging/upload-image", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			const mediaUrl = resolveUploadedFileUrl(file, response);
			setScheduleData((prevState) => ({
				...prevState,
				mediaPath: mediaUrl,
			}));
		} catch (err) {
			toastError(err);
		} finally {
			event.target.value = "";
		}
	};

	const handleConnectionChange = (event) => {
		const selectedConnection = connectionOptions.find(
			(item) => Number(item.id) === Number(event.target.value)
		);

		setScheduleData((prevState) => ({
			...prevState,
			connectionId: selectedConnection?.id || "",
			connection: selectedConnection || null,
			contactId: isLockedContact ? prevState.contactId : "",
			contact: isLockedContact ? prevState.contact : null,
			templateId: null,
			template: null,
			templateParams: [],
			mediaPath: null,
		}));

		if (!isLockedContact) {
			setContactQuery("");
			setContacts([]);
		}
		setTemplateParams([]);
		setTemplateParamsLength(0);
	};

	const handleSelectContact = (selectedContact) => {
		setScheduleData((prevState) => ({
			...prevState,
			contactId: selectedContact.id,
			contact: selectedContact,
		}));
		setContactQuery(renderContactLabel(selectedContact));
		setContacts([]);
	};

	const handleTemplateParamTypeChange = (index, value) => {
		const nextParams = [...templateParams];
		nextParams[index] = value === "contactName" ? "contactName" : "";
		setTemplateParams(nextParams);
		setScheduleData((prevState) => ({
			...prevState,
			templateParams: nextParams,
		}));
	};

	const handleTemplateParamValueChange = (index, value) => {
		const nextParams = [...templateParams];
		nextParams[index] = value;
		setTemplateParams(nextParams);
		setScheduleData((prevState) => ({
			...prevState,
			templateParams: nextParams,
		}));
	};

	const handleSaveSchedule = async () => {
		const parsedContactId =
			scheduleData?.contactId != null && scheduleData.contactId !== ""
				? Number(scheduleData.contactId)
				: null;

		if (!parsedContactId || Number.isNaN(parsedContactId)) {
			toast.error(i18n.t("scheduleModal.contactRequired"));
			return;
		}

		setIsSubmitting(true);
		try {
			const sendAtIso = scheduleData?.sendAt
				? moment(scheduleData.sendAt).toISOString()
				: null;

			const payload = {
				companyId: user?.companyId != null ? +user.companyId : null,
				body: isTemplateMode
					? buildScheduleTemplatePreview(scheduleData?.template, templateParams)
					: String(scheduleData?.body || ""),
				sendAt: sendAtIso,
				contactId: parsedContactId,
				ticketId: scheduleData?.ticketId ? +scheduleData.ticketId : null,
				userId: user?.id ? +user.id : null,
				connectionId: scheduleData?.connectionId
					? +scheduleData.connectionId
					: null,
				templateId: scheduleData?.templateId
					? String(scheduleData.templateId)
					: null,
				mediaPath: scheduleData?.mediaPath ? String(scheduleData.mediaPath) : null,
				templateParams:
					templateParamSlots.length > 0 ? JSON.stringify(templateParams) : null,
			};

			if (scheduleId) {
				await apiV2.put(`/schedules/${scheduleId}`, payload, {
					params: { companyId: user.companyId },
				});
			} else {
				await apiV2.post("/schedules", payload);
			}

			toast.success(i18n.t("scheduleModal.success"));
			if (typeof reload === "function") {
				await reload();
			}
			handleClose();
		} catch (err) {
			toastError(err);
		} finally {
			setIsSubmitting(false);
		}
	};

	const canSubmit =
		!isSubmitting &&
		!loading &&
		!loadingConnections &&
		Boolean(scheduleData?.connectionId) &&
		Boolean(scheduleData?.contactId) &&
		(!isTemplateMode || Boolean(scheduleData?.templateId)) &&
		(!requiresTemplateMedia || Boolean(scheduleData?.mediaPath)) &&
		(templateParamSlots.length === 0 ||
			(templateParams.length === templateParamSlots.length &&
				templateParams.every((value) => String(value || "").trim())));

	const title = scheduleId
		? i18n.t("scheduleModal.title.edit")
		: i18n.t("scheduleModal.title.add");

	const footer = (
		<>
			<button
				type="button"
				className="btn btn-sub-gray"
				onClick={handleClose}
				disabled={isSubmitting}
			>
				{i18n.t("scheduleModal.buttons.cancel")}
			</button>
			{!scheduleData?.sentAt && (
				<button
					type="button"
					className="btn btn-primary"
					onClick={handleSaveSchedule}
					disabled={!canSubmit}
				>
					{scheduleId
						? i18n.t("scheduleModal.buttons.okEdit")
						: i18n.t("scheduleModal.buttons.okAdd")}
				</button>
			)}
		</>
	);

	return (
		<>
			<VariablesModal
				open={variablesModalOpen}
				handleClose={() => setVariablesModalOpen(false)}
				variables={defaultVariables}
				scheduleData={scheduleData}
				setScheduleData={setScheduleData}
			/>

			<AppFullscreenModal
				open={open}
				onClose={handleClose}
				title={title}
				subtitle={i18n.t("scheduleModal.subtitle")}
				maxWidth={1080}
				footer={footer}
			>
				{loading ? (
					<div className="app-modal-loading">
						<div className="app-modal-spinner" />
					</div>
				) : (
					<div className="space-y-4">
						<div className="grid gap-4 lg:grid-cols-2">
							<div className="rounded-lg border border-default-200 bg-white p-4 dark:border-default-700 dark:bg-default-900">
								<AuthField
									id="schedule-connection"
									label={i18n.t("scheduleModal.form.connection")}
									hint={i18n.t("scheduleModal.form.connectionHint")}
									className="mb-0"
								>
									<select
										id="schedule-connection"
										className="form-select w-full"
										value={scheduleData?.connectionId || ""}
										onChange={handleConnectionChange}
										disabled={loadingConnections || isSubmitting}
									>
										<option value="">
											{i18n.t("scheduleModal.form.connection")}
										</option>
										{whatsappConnections.map((connectionOption) => (
											<option key={connectionOption.id} value={connectionOption.id}>
												{`${connectionOption.name} (${connectionOption.channel})`}
											</option>
										))}
									</select>
								</AuthField>
							</div>

							<div className="rounded-lg border border-default-200 bg-white p-4 dark:border-default-700 dark:bg-default-900">
								<AuthField
									id="schedule-send-at"
									label={i18n.t("scheduleModal.form.sendAt")}
									className="mb-0"
								>
									<input
										id="schedule-send-at"
										type="datetime-local"
										className="form-input w-full"
										value={
											scheduleData?.sendAt || moment().format("YYYY-MM-DDTHH:mm")
										}
										onChange={(event) =>
											setScheduleData((prevState) => ({
												...prevState,
												sendAt: event.target.value,
											}))
										}
									/>
								</AuthField>
								{scheduleData?.status && (
									<p className="mb-0 mt-3 text-sm text-default-500 dark:text-default-400">
										{i18n.t("scheduleModal.statusLabel")}:{" "}
										<span className="font-medium text-default-700 dark:text-default-200">
											{capitalize(String(scheduleData.status).toLowerCase())}
										</span>
									</p>
								)}
							</div>
						</div>

						<div className="rounded-lg border border-default-200 bg-white p-4 dark:border-default-700 dark:bg-default-900">
							<AuthField
								id="schedule-contact"
								label={i18n.t("scheduleModal.form.contact")}
								className="mb-0"
							>
								{isLockedContact ? (
									<input
										id="schedule-contact"
										type="text"
										className="form-input w-full"
										value={contactQuery || scheduleData?.contact?.name || ""}
										disabled
									/>
								) : (
									<div className="space-y-3">
										<input
											id="schedule-contact"
											type="text"
											className="form-input w-full"
											placeholder={i18n.t("scheduleModal.form.searchContact")}
											value={contactQuery}
											onChange={(event) => {
												const nextValue = event.target.value;
												setContactQuery(nextValue);
												setScheduleData((prevState) => ({
													...prevState,
													contactId:
														renderContactLabel(prevState.contact) === nextValue
															? prevState.contactId
															: "",
													contact:
														renderContactLabel(prevState.contact) === nextValue
															? prevState.contact
															: null,
												}));
											}}
											disabled={!scheduleData?.connectionId}
										/>
										{!scheduleData?.connectionId ? (
											<p className="m-0 text-sm text-default-500 dark:text-default-400">
												{i18n.t("scheduleModal.form.connectionHint")}
											</p>
										) : contactQuery.trim().length < 3 ? (
											<p className="m-0 text-sm text-default-500 dark:text-default-400">
												{i18n.t("scheduleModal.form.searchContactHint")}
											</p>
										) : loadingContacts ? (
											<div className="inline-flex items-center gap-2 text-sm text-default-500 dark:text-default-400">
												<div className="app-modal-spinner !size-4" />
												<span>{i18n.t("scheduleModal.form.searchContact")}</span>
											</div>
										) : contacts.length > 0 ? (
											<div className="max-h-56 overflow-y-auto rounded-md border border-default-200 dark:border-default-700">
												{contacts.map((contactOption) => (
													<button
														key={contactOption.id}
														type="button"
														className="flex w-full flex-col gap-0.5 border-b border-default-200 px-3 py-2 text-start last:border-b-0 hover:bg-default-100 dark:border-default-700 dark:hover:bg-default-800"
														onClick={() => handleSelectContact(contactOption)}
													>
														<span className="text-sm font-medium text-default-800 dark:text-default-100">
															{contactOption.name}
														</span>
														<span className="text-xs text-default-500 dark:text-default-400">
															{renderContactLabel(contactOption)}
														</span>
													</button>
												))}
											</div>
										) : (
											<p className="m-0 text-sm text-default-500 dark:text-default-400">
												{i18n.t("scheduleModal.form.contactSearchEmpty")}
											</p>
										)}
									</div>
								)}
							</AuthField>
						</div>

						{isTemplateMode ? (
							<div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)] lg:items-start">
							<div className="space-y-4 rounded-lg border border-default-200 bg-white p-4 dark:border-default-700 dark:bg-default-900">
								<div>
									<h6 className="mb-1 text-sm font-semibold text-default-800 dark:text-default-100">
										{i18n.t("scheduleModal.form.templateSection")}
									</h6>
									<p className="m-0 text-sm text-default-500 dark:text-default-400">
										{i18n.t("scheduleModal.form.templateSectionHint")}
									</p>
								</div>

								<TemplatesTable
									whatsappId={scheduleData?.connectionId}
									scheduleData={scheduleData}
									setScheduleData={setScheduleData}
									setTemplateParamsLength={setTemplateParamsLength}
									onTemplateResolved={handleTemplateResolved}
								/>

								{templateParamSlots.length > 0 && (
									<div className="space-y-3">
										<h6 className="mb-0 text-sm font-semibold text-default-800 dark:text-default-100">
											{i18n.t("scheduleModal.form.templateParams")} ({templateParamSlots.length})
										</h6>
										{templateParamSlots.map((slot, index) => (
											<div
												key={`template-param-${slot.kind}-${index}`}
												className="grid gap-3 rounded-md border border-default-200 p-3 dark:border-default-700 md:grid-cols-[220px_minmax(0,1fr)]"
											>
												<div>
													<p className="mb-2 text-xs font-medium text-default-500">
														{slot.label}
													</p>
													<select
														className="form-select w-full"
														value={
															templateParams[index] === "contactName"
																? "contactName"
																: "text"
														}
														onChange={(event) =>
															handleTemplateParamTypeChange(index, event.target.value)
														}
													>
														<option value="text">
															{i18n.t("scheduleModal.form.paramText")}
														</option>
														<option value="contactName">
															{i18n.t("scheduleModal.form.paramContactName")}
														</option>
													</select>
												</div>

												{templateParams[index] !== "contactName" ? (
													<input
														type="text"
														className="form-input w-full"
														placeholder={slot.label}
														value={templateParams[index] || ""}
														onChange={(event) =>
															handleTemplateParamValueChange(index, event.target.value)
														}
													/>
												) : (
													<div className="flex items-center rounded-md border border-default-200 bg-default-100 px-3 text-sm text-default-500 dark:border-default-700 dark:bg-default-800 dark:text-default-300">
														{i18n.t("scheduleModal.form.paramContactName")}
													</div>
												)}
											</div>
										))}
									</div>
								)}

								{requiresTemplateMedia && (
									<AuthField
										id="schedule-template-media"
										label={i18n.t("scheduleModal.form.media")}
										hint={i18n.t("scheduleModal.form.mediaHint")}
										className="mb-0"
									>
										<div className="space-y-3">
											{scheduleData?.mediaPath && (
												<div className="rounded-md border border-default-200 bg-default-100 p-3 dark:border-default-700 dark:bg-default-800">
													<div className="flex flex-wrap items-start justify-between gap-3">
														<div className="min-w-0 flex-1">
															{isImageUrl(scheduleData.mediaPath) ? (
																<div className="mb-3 max-w-[160px] overflow-hidden rounded-md">
																	<ModalImageChatbot imageUrl={scheduleData.mediaPath} />
																</div>
															) : null}
															<p className="mb-0 truncate text-sm text-default-600 dark:text-default-300">
																{scheduleData.mediaPath}
															</p>
														</div>
														<button
															type="button"
															className="btn btn-sm btn-sub-gray"
															onClick={() =>
																setScheduleData((prevState) => ({
																	...prevState,
																	mediaPath: null,
																}))
															}
														>
															{i18n.t("scheduleModal.form.removeFile")}
														</button>
													</div>
												</div>
											)}

											<label className="btn btn-sub-gray inline-flex w-fit cursor-pointer">
												<IconAttach />
												{i18n.t("scheduleModal.form.attachFile")}
												<input
													type="file"
													className="sr-only"
													accept={mediaAccept(currentTemplateFormat)}
													onChange={handleUpdateMedia}
												/>
											</label>
										</div>
									</AuthField>
								)}
							</div>
							<aside className="lg:sticky lg:top-0">
								<TemplateConversationPreview
									template={scheduleData?.template || null}
									params={templateParams}
									mediaPath={scheduleData?.mediaPath || null}
									darkMode={darkMode}
								/>
							</aside>
							</div>
						) : (
							<div className="space-y-4 rounded-lg border border-default-200 bg-white p-4 dark:border-default-700 dark:bg-default-900">
								<div>
									<div className="mb-2 flex items-center justify-between gap-3">
										<label
											htmlFor="schedule-body"
											className="block text-sm font-medium text-default-800 dark:text-default-100"
										>
											{i18n.t("scheduleModal.form.body")}
										</label>
										<span className="text-xs text-default-500 dark:text-default-400">
											{`${scheduleData?.body?.length || 0}/1024`}
										</span>
									</div>
									<textarea
										id="schedule-body"
										className="form-input min-h-[8rem] w-full resize-y"
										value={scheduleData?.body || ""}
										maxLength={1024}
										onChange={handleMessage}
									/>
									<p className="mb-0 mt-2 text-sm text-default-500 dark:text-default-400">
										{i18n.t("scheduleModal.form.messageHint")}
									</p>
								</div>

								<AuthField
									id="schedule-media"
									label={i18n.t("scheduleModal.form.media")}
									hint={i18n.t("scheduleModal.form.mediaHint")}
									className="mb-0"
								>
									<div className="space-y-3">
										{scheduleData?.mediaPath && (
											<div className="rounded-md border border-default-200 bg-default-100 p-3 dark:border-default-700 dark:bg-default-800">
												<div className="flex flex-wrap items-start justify-between gap-3">
													<div className="min-w-0 flex-1">
														{isImageUrl(scheduleData.mediaPath) ? (
															<div className="mb-3 max-w-[160px] overflow-hidden rounded-md">
																<ModalImageChatbot imageUrl={scheduleData.mediaPath} />
															</div>
														) : null}
														<p className="mb-0 truncate text-sm text-default-600 dark:text-default-300">
															{scheduleData.mediaPath}
														</p>
													</div>
													<button
														type="button"
														className="btn btn-sm btn-sub-gray"
														onClick={() =>
															setScheduleData((prevState) => ({
																...prevState,
																mediaPath: null,
															}))
														}
													>
														{i18n.t("scheduleModal.form.removeFile")}
													</button>
												</div>
											</div>
										)}

										<label className="btn btn-sub-gray inline-flex w-fit cursor-pointer">
											<IconAttach />
											{i18n.t("scheduleModal.form.attachFile")}
											<input
												type="file"
												className="sr-only"
												accept={mediaAccept()}
												onChange={handleUpdateMedia}
											/>
										</label>
									</div>
								</AuthField>
							</div>
						)}
					</div>
				)}
			</AppFullscreenModal>
		</>
	);
}

export default ScheduleModal;
